import React, { useState, useMemo } from 'react';
import {
  Container,
  Box,
  TextField,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Snackbar,
  Typography,
  Chip,
  Tooltip,
  Paper,
  Divider,
  InputAdornment,
  CircularProgress,
  Avatar
} from '@mui/material';
import dayjs from 'dayjs';
import Grid from '@mui/material/Grid'; 
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { 
  Edit, 
  Delete, 
  Refresh,
  FilterList,
  OpenInNew,
  Search,
  Save,
  Close,
  People,
  School,
  Security,
  Clear,
  Warning,
  CheckCircle,
  Schedule
} from '@mui/icons-material';
import { TrainingRecord } from '../types';
import { ApiService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const GridAny = Grid as any;

interface TrainingDataTableProps {
  data: TrainingRecord[];
  onRefresh: () => void;
  userRole: string | null;
}

const TrainingDataTable: React.FC<TrainingDataTableProps> = ({ data, onRefresh, userRole }) => {
  const { user } = useAuth();
  
  // 상태 관리
  const [searchText, setSearchText] = useState('');
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<TrainingRecord | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [snackbar, setSnackbar] = useState<{ 
    open: boolean; 
    message: string; 
    severity: 'success' | 'error' 
  }>({
    open: false,
    message: '',
    severity: 'success'
  });

  // 필터 상태
  const [teamFilter, setTeamFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const canEdit = userRole === 'developer' || userRole === 'admin';

  // 고유 팀/지점 목록 추출
  const uniqueTeams = useMemo(() => {
    const teams = data
      .map(record => record.소속팀지점)
      .filter(team => team && team.trim() !== '')
      .filter((team, index, self) => self.indexOf(team) === index)
      .sort();
    return teams;
  }, [data]);

  // 필터링된 데이터
  const filteredData = useMemo(() => {
    return data.filter(record => {
      const matchesSearch = searchText === '' || 
        Object.values(record).some(value =>
          value?.toString().toLowerCase().includes(searchText.toLowerCase())
        );
      
      const matchesTeam = teamFilter === '' || record.소속팀지점 === teamFilter;
      const matchesType = typeFilter === '' || record.초기정기구분 === typeFilter;
      const matchesStatus = statusFilter === '' || record.처리상태 === statusFilter;

      return matchesSearch && matchesTeam && matchesType && matchesStatus;
    });
  }, [data, searchText, teamFilter, typeFilter, statusFilter]);

  // 필터 초기화
  const handleResetFilters = () => {
    setSearchText('');
    setTeamFilter('');
    setTypeFilter('');
    setStatusFilter('');
  };

  // 핸들러 함수들
  const handleEdit = (record: TrainingRecord) => {
    setSelectedRecord({ ...record });
    setEditDialogOpen(true);
  };

  const handleSave = async () => {
    if (!selectedRecord || !user?.email) return;
    setIsSaving(true);
    try {
      const response = await ApiService.updateRecord(
        user.email,
        selectedRecord.RecordID,
        {
          성명: selectedRecord.성명,
          수료번호: selectedRecord.수료번호,
          수료일자: selectedRecord.수료일자,
          수료기관: selectedRecord.수료기관,
          소속팀지점: selectedRecord.소속팀지점,
          초기정기구분: selectedRecord.초기정기구분,
          보안책임자감독자: selectedRecord.보안책임자감독자,
          처리상태: selectedRecord.처리상태,
          비고: selectedRecord.비고
        }
      );
      if (response.status === 'success') {
        setSnackbar({ 
          open: true, 
          message: '✅ 성공적으로 수정되었습니다!', 
          severity: 'success' 
        });
        setEditDialogOpen(false);
        onRefresh();
      } else {
        setSnackbar({ 
          open: true, 
          message: '❌ ' + (response.message || '수정 실패'), 
          severity: 'error' 
        });
      }
    } catch (error: any) {
      setSnackbar({ 
        open: true, 
        message: '❌ 수정 실패: ' + error.message, 
        severity: 'error' 
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (recordId: string) => {
    if (!user?.email) return;
    if (!window.confirm('정말 삭제하시겠습니까?\n이 작업은 되돌릴 수 없습니다.')) return;
    try {
      const response = await ApiService.deleteRecord(user.email, recordId);
      if (response.status === 'success') {
        setSnackbar({ open: true, message: '✅ 삭제되었습니다', severity: 'success' });
        onRefresh();
      } else {
        setSnackbar({ open: true, message: '❌ ' + (response.message || '삭제 실패'), severity: 'error' });
      }
    } catch (error: any) {
      setSnackbar({ open: true, message: '❌ 삭제 실패: ' + error.message, severity: 'error' });
    }
  };

  // 컬럼 정의 - DataGrid 오른쪽 빈 공간 완전 제거
  const columns: GridColDef[] = [
    { 
      field: '수료번호', 
      headerName: '수료번호', 
      width: 180,
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2" sx={{ 
          fontFamily: 'monospace', 
          fontWeight: 600, 
          color: '#344767',
          bgcolor: '#f8f9fa',
          px: 1,
          py: 0.5,
          borderRadius: 1,
          fontSize: '0.85rem'
        }}>
          {params.value}
        </Typography>
      )
    },
    { 
      field: '성명', 
      headerName: '성명', 
      width: 110,
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2" sx={{ fontWeight: 600, color: '#344767' }}>
          {params.value}
        </Typography>
      )
    },
    { 
      field: '소속팀지점', 
      headerName: '팀/지점', 
      width: 150,
      renderCell: (params: GridRenderCellParams) => (
        <Chip 
          label={params.value || '미분류'} 
          size="small" 
          variant="outlined"
          sx={{ 
            borderColor: params.value ? '#667eea' : '#e0e0e0', 
            bgcolor: params.value ? 'rgba(102,126,234,0.1)' : '#f8f9fa',
            color: params.value ? '#667eea' : '#7b809a',
            fontWeight: 600
          }}
        />
      )
    },
    { 
      field: '초기정기구분', 
      headerName: '구분', 
      width: 100,
      renderCell: (params: GridRenderCellParams) => (
        <Chip 
          label={params.value || '확인필요'} 
          size="small" 
          sx={{ 
            bgcolor: params.value === '초기' ? '#e91e63' : params.value === '정기' ? '#1a73e8' : '#e0e0e0',
            color: params.value === '확인필요' ? '#344767' : 'white',
            fontWeight: 600,
            height: 24
          }}
        />
      )
    },
    { 
      field: '보안책임자감독자', 
      headerName: '역할', 
      width: 140,
      renderCell: (params: GridRenderCellParams) => (
        <Chip 
          label={params.value || '확인필요'} 
          size="small" 
          sx={{ 
            bgcolor: params.value?.includes('책임자') ? '#fb8c00' : params.value?.includes('감독자') ? '#344767' : '#e0e0e0',
            color: params.value === '확인필요' ? '#344767' : 'white',
            fontWeight: 600,
            height: 24
          }}
        />
      )
    },
    { field: '수료일자', headerName: '수료일자', width: 120 },
    { 
      field: '유효만료일', 
      headerName: '유효만료일', 
      width: 120,
      renderCell: (params: GridRenderCellParams) => {
        const 수료일자 = params.row.수료일자;
        if (!수료일자) return <Typography variant="body2" sx={{ color: '#9e9e9e' }}>-</Typography>;
        
        const 만료일 = dayjs(수료일자).add(395, 'day');
        return (
          <Typography variant="body2" sx={{ color: '#344767', fontWeight: 500 }}>
            {만료일.format('YYYY-MM-DD')}
          </Typography>
        );
      }
    },
    { 
      field: '잔여일', 
      headerName: '잔여일', 
      width: 110,
      renderCell: (params: GridRenderCellParams) => {
        const 수료일자 = params.row.수료일자;
        if (!수료일자) return <Typography variant="body2" sx={{ color: '#9e9e9e' }}>-</Typography>;
        
        // 한국 표준시 기준 오늘 날짜
        const 오늘 = dayjs().startOf('day');
        const 만료일 = dayjs(수료일자).add(395, 'day').startOf('day');
        const 잔여일수 = 만료일.diff(오늘, 'day');
        
        // 상태별 스타일 정의
        let bgColor = '#e8f5e9';
        let textColor = '#2e7d32';
        let icon = <CheckCircle sx={{ fontSize: 16, mr: 0.5 }} />;
        let fontWeight = 600;
        
        if (잔여일수 < 0) {
          // 만료됨
          bgColor = '#ffebee';
          textColor = '#c62828';
          icon = <Warning sx={{ fontSize: 16, mr: 0.5 }} />;
          fontWeight = 700;
        } else if (잔여일수 <= 30) {
          // 30일 이내 - 긴급
          bgColor = '#fff3e0';
          textColor = '#e65100';
          icon = <Warning sx={{ fontSize: 16, mr: 0.5 }} />;
          fontWeight = 700;
        } else if (잔여일수 <= 60) {
          // 60일 이내 - 주의
          bgColor = '#fffde7';
          textColor = '#f9a825';
          icon = <Schedule sx={{ fontSize: 16, mr: 0.5 }} />;
        }
        
        return (
          <Tooltip 
            title={잔여일수 < 0 
              ? `⚠️ ${Math.abs(잔여일수)}일 전 만료됨! 재교육 필요` 
              : 잔여일수 <= 30 
                ? `🚨 긴급! ${잔여일수}일 남음 - 즉시 재교육 필요`
                : 잔여일수 <= 60
                  ? `⚠️ 주의! ${잔여일수}일 남음 - 재교육 계획 필요`
                  : `✅ ${잔여일수}일 남음`
            }
            arrow
          >
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center',
              bgcolor: bgColor,
              color: textColor,
              px: 1.5,
              py: 0.5,
              borderRadius: 2,
              fontWeight: fontWeight,
              fontSize: '0.85rem',
              minWidth: 80,
              justifyContent: 'center',
              border: 잔여일수 <= 30 ? `2px solid ${textColor}` : 'none',
              animation: 잔여일수 <= 30 && 잔여일수 >= 0 ? 'pulse 2s infinite' : 'none',
              '@keyframes pulse': {
                '0%': { opacity: 1 },
                '50%': { opacity: 0.7 },
                '100%': { opacity: 1 }
              }
            }}>
              {icon}
              {잔여일수 < 0 ? `${Math.abs(잔여일수)}일 초과` : `${잔여일수}일`}
            </Box>
          </Tooltip>
        );
      }
    },
    { 
      field: '수료기관', 
      headerName: '수료기관', 
      flex: 1, // 🔥 핵심: 남은 공간 모두 차지
      minWidth: 180 
    },
    { 
      field: '처리상태', 
      headerName: '상태', 
      width: 120,
      renderCell: (params: GridRenderCellParams) => (
        <Chip 
          label={params.value || '확인필요'} 
          size="small" 
          sx={{
            fontWeight: 600,
            color: params.value === '완료' ? '#4caf50' : params.value === '확인필요' ? '#e91e63' : '#344767',
            bgcolor: params.value === '완료' ? '#e8f5e9' : params.value === '확인필요' ? '#fce4ec' : '#f8f9fa'
          }}
        />
      )
    },
    {
      field: 'actions',
      headerName: '작업',
      width: 140,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => (
        <Box>
          <Tooltip title="PDF 보기">
            <IconButton 
              size="small" 
              onClick={() => window.open(params.row.PDF링크, '_blank')}
              sx={{ color: '#1a73e8' }}
            >
              <OpenInNew fontSize="small" />
            </IconButton>
          </Tooltip>
          {canEdit && (
            <>
              <Tooltip title="수정">
                <IconButton 
                  size="small" 
                  onClick={() => handleEdit(params.row as TrainingRecord)}
                  sx={{ color: '#344767' }}
                >
                  <Edit fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="삭제">
                <IconButton 
                  size="small" 
                  onClick={() => handleDelete(params.row.RecordID)}
                  color="error"
                >
                  <Delete fontSize="small" />
                </IconButton>
              </Tooltip>
            </>
          )}
        </Box>
      ),
    },
  ];

  return (
    <Container maxWidth={false} sx={{ mt: 2, mb: 4, px: { xs: 2, md: 4, xl: 6 } }}>
      
      {/* 🔥 1920x1080 완벽한 1줄 레이아웃 - 안정성 극대화 버전 */}
      <Paper sx={{ 
        p: 4, 
        mb: 3, 
        borderRadius: 3, 
        boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
        border: '1px solid #e9ecef',
        background: 'linear-gradient(to right, #ffffff, #f8f9fa)',
        width: '100%'
      }}>
        
        {/* 헤더 영역 */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          mb: 3
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ 
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              p: 1.5,
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <Search sx={{ color: 'white', fontSize: 28 }} />
            </Box>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#344767', mb: 0.5 }}>
                🔍 검색 및 필터
              </Typography>
              <Typography variant="body2" sx={{ color: '#7b809a' }}>
                Search & Filter Panel - Perfect Single Row Layout FINAL
              </Typography>
            </Box>
          </Box>
          <Chip 
            label={`총 ${data.length}건 중 ${filteredData.length}건 표시`}
            size="medium"
            sx={{ 
              bgcolor: '#e3f2fd', 
              color: '#1a73e8', 
              fontWeight: 700,
              px: 3,
              py: 2.5,
              fontSize: '0.95rem',
              height: 'auto'
            }}
          />
        </Box>
        
        <Divider sx={{ mb: 3, borderColor: '#e9ecef' }} />
        
        {/* 🔥 핵심: 5+2+2+2+1 = 12칸 완벽한 1줄 Grid - 안정성 극대화 */}
        <Box sx={{ width: '100%', overflow: 'hidden' }}>
          <GridAny 
            container 
            spacing={2}
            columns={12} // 🔥 명시적으로 12칸 지정
            sx={{ 
              flexWrap: 'nowrap', // 🔥 줄바꿈 절대 금지
              alignItems: 'center',
              width: '100%',
              minWidth: 0,
              '& .MuiGrid-item': {
                flexShrink: 0, // 🔥 축소 방지
                minWidth: 0
              }
            }}
          >
            
            {/* 1. 통합 검색 - 5칸 (41.7% = 800px@1920px) */}
            <GridAny item xs={5}>
              <TextField
                fullWidth
                variant="outlined"
                size="medium"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                placeholder="🔎 통합 검색: 이름, 수료번호, 팀/지점, 수료기관 등..."
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search sx={{ color: '#667eea', fontSize: 24 }} />
                    </InputAdornment>
                  ),
                  endAdornment: searchText && (
                    <InputAdornment position="end">
                      <IconButton 
                        size="small" 
                        onClick={() => setSearchText('')}
                        sx={{ color: '#7b809a' }}
                      >
                        <Clear />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{ 
                  bgcolor: 'white',
                  minWidth: 300, // 🔥 최소 너비 보장
                  '& .MuiOutlinedInput-root': { 
                    borderRadius: 2,
                    minHeight: 56,
                    '& fieldset': { borderWidth: 2, borderColor: '#e9ecef' },
                    '&:hover fieldset': { borderColor: '#667eea' },
                    '&.Mui-focused fieldset': { borderColor: '#667eea' }
                  }
                }}
              />
            </GridAny>
            
            {/* 2. 팀/지점 - 2칸 (16.7% = 320px@1920px) */}
            <GridAny item xs={2}>
              <FormControl fullWidth size="medium">
                <InputLabel sx={{ fontWeight: 600 }}>팀/지점</InputLabel>
                <Select
                  value={teamFilter}
                  onChange={(e) => setTeamFilter(e.target.value)}
                  label="팀/지점"
                  sx={{ 
                    borderRadius: 2,
                    bgcolor: 'white',
                    minHeight: 56,
                    minWidth: 140, // 🔥 최소 너비 보장
                    '& .MuiOutlinedInput-notchedOutline': { borderWidth: 2 }
                  }}
                >
                  <MenuItem value="">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#e0e0e0' }} />
                      <em>전체</em>
                    </Box>
                  </MenuItem>
                  {uniqueTeams.map((team) => (
                    <MenuItem key={team} value={team}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#667eea' }} />
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {team}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </GridAny>
            
            {/* 3. 초기/정기 - 2칸 (16.7% = 320px@1920px) */}
            <GridAny item xs={2}>
              <FormControl fullWidth size="medium">
                <InputLabel sx={{ fontWeight: 600 }}>초기/정기</InputLabel>
                <Select
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value)}
                  label="초기/정기"
                  sx={{ 
                    borderRadius: 2,
                    bgcolor: 'white',
                    minHeight: 56,
                    minWidth: 140, // 🔥 최소 너비 보장
                    '& .MuiOutlinedInput-notchedOutline': { borderWidth: 2 }
                  }}
                >
                  <MenuItem value="">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#e0e0e0' }} />
                      <em>전체</em>
                    </Box>
                  </MenuItem>
                  <MenuItem value="초기">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Chip 
                        label="초기" 
                        size="small" 
                        sx={{ bgcolor: '#e91e63', color: 'white', fontWeight: 600, width: 50 }} 
                      />
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        초기교육
                      </Typography>
                    </Box>
                  </MenuItem>
                  <MenuItem value="정기">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Chip 
                        label="정기" 
                        size="small" 
                        sx={{ bgcolor: '#1a73e8', color: 'white', fontWeight: 600, width: 50 }} 
                      />
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        정기교육
                      </Typography>
                    </Box>
                  </MenuItem>
                </Select>
              </FormControl>
            </GridAny>
            
            {/* 4. 처리상태 - 2칸 (16.7% = 320px@1920px) */}
            <GridAny item xs={2}>
              <FormControl fullWidth size="medium">
                <InputLabel sx={{ fontWeight: 600 }}>처리상태</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  label="처리상태"
                  sx={{ 
                    borderRadius: 2,
                    bgcolor: 'white',
                    minHeight: 56,
                    minWidth: 140, // 🔥 최소 너비 보장
                    '& .MuiOutlinedInput-notchedOutline': { borderWidth: 2 }
                  }}
                >
                  <MenuItem value="">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#e0e0e0' }} />
                      <em>전체</em>
                    </Box>
                  </MenuItem>
                  <MenuItem value="완료">✅ 완료</MenuItem>
                  <MenuItem value="자동처리">🤖 자동처리</MenuItem>
                  <MenuItem value="수동입력">✍️ 수동입력</MenuItem>
                  <MenuItem value="확인필요">⚠️ 확인필요</MenuItem>
                </Select>
              </FormControl>
            </GridAny>
            
            {/* 5. 버튼 - 1칸 (8.3% = 160px@1920px) */}
            <GridAny item xs={1}>
              <Box sx={{ 
                display: 'flex', 
                gap: 1, 
                justifyContent: 'flex-end',
                minWidth: 120 // 🔥 버튼 영역 최소 너비 보장
              }}>
                <Tooltip title="필터 초기화">
                  <IconButton 
                    onClick={handleResetFilters}
                    sx={{ 
                      width: 56,
                      height: 56,
                      borderRadius: 2,
                      border: '2px solid #e0e0e0',
                      color: '#7b809a',
                      bgcolor: 'white',
                      flexShrink: 0, // 🔥 축소 방지
                      '&:hover': { 
                        borderColor: '#7b809a',
                        bgcolor: '#f8f9fa'
                      }
                    }}
                  >
                    <FilterList />
                  </IconButton>
                </Tooltip>
                <Tooltip title="데이터 새로고침">
                  <IconButton 
                    onClick={onRefresh}
                    sx={{ 
                      width: 56,
                      height: 56,
                      borderRadius: 2,
                      bgcolor: '#344767',
                      color: 'white',
                      flexShrink: 0, // 🔥 축소 방지
                      '&:hover': { bgcolor: '#212529' }
                    }}
                  >
                    <Refresh />
                  </IconButton>
                </Tooltip>
              </Box>
            </GridAny>
          </GridAny>
        </Box>

        {/* 활성 필터 표시 */}
        {(searchText || teamFilter || typeFilter || statusFilter) && (
          <Box sx={{ mt: 3, pt: 3, borderTop: '1px solid #e9ecef' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#7b809a' }}>
                🎯 활성 필터:
              </Typography>
              {searchText && (
                <Chip
                  label={`검색: "${searchText}"`}
                  onDelete={() => setSearchText('')}
                  size="medium"
                  sx={{ 
                    bgcolor: '#e3f2fd', 
                    color: '#1a73e8', 
                    fontWeight: 600,
                    '& .MuiChip-deleteIcon': { color: '#1a73e8' }
                  }}
                />
              )}
              {teamFilter && (
                <Chip
                  label={`팀: ${teamFilter}`}
                  onDelete={() => setTeamFilter('')}
                  size="medium"
                  sx={{ 
                    bgcolor: 'rgba(102,126,234,0.1)', 
                    color: '#667eea', 
                    fontWeight: 600,
                    '& .MuiChip-deleteIcon': { color: '#667eea' }
                  }}
                />
              )}
              {typeFilter && (
                <Chip
                  label={`구분: ${typeFilter}`}
                  onDelete={() => setTypeFilter('')}
                  size="medium"
                  sx={{ 
                    bgcolor: typeFilter === '초기' ? '#fce4ec' : '#e3f2fd', 
                    color: typeFilter === '초기' ? '#e91e63' : '#1a73e8', 
                    fontWeight: 600,
                    '& .MuiChip-deleteIcon': {
                      color: typeFilter === '초기' ? '#e91e63' : '#1a73e8'
                    }
                  }}
                />
              )}
              {statusFilter && (
                <Chip
                  label={`상태: ${statusFilter}`}
                  onDelete={() => setStatusFilter('')}
                  size="medium"
                  sx={{ 
                    bgcolor: '#e8f5e9', 
                    color: '#4caf50', 
                    fontWeight: 600,
                    '& .MuiChip-deleteIcon': { color: '#4caf50' }
                  }}
                />
              )}
            </Box>
          </Box>
        )}
      </Paper>

      {/* DataGrid */}
      <Paper sx={{ 
        height: 'calc(100vh - 320px)',
        minHeight: 650,
        width: '100%', 
        borderRadius: 3, 
        boxShadow: '0 4px 20px rgba(0,0,0,0.05)', 
        overflow: 'hidden' 
      }}>
        <DataGrid
          rows={filteredData}
          columns={columns}
          getRowId={(row) => row.RecordID}
          initialState={{
            pagination: { paginationModel: { pageSize: 25 } },
            sorting: { sortModel: [{ field: '수료일자', sort: 'desc' }] },
          }}
          pageSizeOptions={[10, 25, 50, 100]}
          disableRowSelectionOnClick
          sx={{
            border: 'none',
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: '#f8f9fa',
              color: '#7b809a',
              fontWeight: 700,
              fontSize: '0.9rem',
              borderBottom: '2px solid #e9ecef'
            },
            '& .MuiDataGrid-cell': {
              borderBottom: '1px solid #f0f2f5',
              color: '#344767',
              fontSize: '0.9rem'
            },
            '& .MuiDataGrid-row:hover': {
              backgroundColor: '#f8f9fa'
            },
            '& .MuiDataGrid-footerContainer': {
              borderTop: '2px solid #e9ecef',
              backgroundColor: '#f8f9fa'
            }
          }}
        />
      </Paper>

      {/* 수정 다이얼로그 (완전 버전) */}
      <Dialog 
        open={editDialogOpen} 
        onClose={() => !isSaving && setEditDialogOpen(false)} 
        maxWidth="lg" 
        fullWidth
        PaperProps={{ 
          sx: { 
            borderRadius: 3, 
            maxHeight: '90vh',
            boxShadow: '0 8px 40px rgba(0,0,0,0.12)'
          } 
        }}
      >
        <DialogTitle sx={{ 
          borderBottom: '2px solid #e9ecef', 
          pb: 2,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white'
        }}>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center" gap={1.5}>
              <Box sx={{ 
                bgcolor: 'rgba(255,255,255,0.2)', 
                borderRadius: 2, 
                p: 1, 
                display: 'flex' 
              }}>
                <Edit />
              </Box>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  ✈️ 보안교육 이수정보 수정
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.9 }}>
                  Aviation Security Training Record Management
                </Typography>
              </Box>
            </Box>
            <IconButton 
              onClick={() => !isSaving && setEditDialogOpen(false)} 
              size="small"
              disabled={isSaving}
              sx={{ color: 'white', '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}
            >
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        
        <DialogContent sx={{ pt: 4, pb: 3, bgcolor: '#fafafa' }}>
          {selectedRecord && (
            <GridAny container spacing={3}>
              {/* 섹션 1: 피교육자 기본정보 - 수정 가능 */}
              <GridAny item xs={12}>
                <Paper sx={{ 
                  p: 3, 
                  borderRadius: 3, 
                  border: '2px solid #e3f2fd',
                  bgcolor: 'white',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
                }}>
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 1.5, 
                    mb: 3,
                    pb: 2,
                    borderBottom: '2px solid #e3f2fd'
                  }}>
                    <Box sx={{ 
                      bgcolor: '#1a73e8', 
                      color: 'white', 
                      p: 1, 
                      borderRadius: 2,
                      display: 'flex'
                    }}>
                      <People />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#344767' }}>
                        👤 피교육자 기본정보
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#7b809a' }}>
                        Trainee Information - 자동 등록 후 관리자가 검토/수정
                      </Typography>
                    </Box>
                    <Chip 
                      label="수정가능" 
                      size="small" 
                      sx={{ 
                        bgcolor: '#e8f5e9', 
                        color: '#4caf50', 
                        fontWeight: 600,
                        fontSize: '0.7rem'
                      }} 
                    />
                  </Box>
                  
                  <GridAny container spacing={2.5}>
                    <GridAny item xs={12} sm={6} md={3}>
                      <TextField
                        label="성명 *"
                        value={selectedRecord.성명 || ''}
                        onChange={(e) => setSelectedRecord({ 
                          ...selectedRecord, 
                          성명: e.target.value 
                        })}
                        fullWidth
                        required
                        disabled={isSaving}
                        placeholder="예: 홍길동"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Avatar sx={{ 
                                width: 24, 
                                height: 24, 
                                bgcolor: '#667eea',
                                fontSize: '0.7rem',
                                fontWeight: 700
                              }}>
                                {(selectedRecord.성명 || '?').charAt(0)}
                              </Avatar>
                            </InputAdornment>
                          ),
                        }}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                        helperText="PDF에서 자동 추출된 이름 확인/수정"
                      />
                    </GridAny>
                    
                    <GridAny item xs={12} sm={6} md={3}>
                      <TextField
                        label="수료번호"
                        value={selectedRecord.수료번호 || ''}
                        onChange={(e) => setSelectedRecord({ 
                          ...selectedRecord, 
                          수료번호: e.target.value 
                        })}
                        fullWidth
                        disabled={isSaving}
                        placeholder="예: KASI-2025-01-1234"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">📋</InputAdornment>
                          ),
                        }}
                        sx={{ 
                          '& .MuiOutlinedInput-root': { borderRadius: 2 },
                          '& input': { fontFamily: 'monospace' }
                        }}
                        helperText="수료증에 기재된 번호 입력"
                      />
                    </GridAny>
                    
                    <GridAny item xs={12} sm={6} md={3}>
                      <TextField
                        label="수료일자 *"
                        type="date"
                        value={selectedRecord.수료일자 ? selectedRecord.수료일자.split('T')[0] : ''}
                        onChange={(e) => setSelectedRecord({ 
                          ...selectedRecord, 
                          수료일자: e.target.value 
                        })}
                        fullWidth
                        required
                        disabled={isSaving}
                        InputLabelProps={{ shrink: true }}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                        helperText="수료증에 기재된 날짜 (유효만료일 자동계산)"
                      />
                    </GridAny>
                    
                    <GridAny item xs={12} sm={6} md={3}>
                      <TextField
                        label="수료기관"
                        value={selectedRecord.수료기관 || ''}
                        onChange={(e) => setSelectedRecord({ 
                          ...selectedRecord, 
                          수료기관: e.target.value 
                        })}
                        fullWidth
                        disabled={isSaving}
                        placeholder="예: 한국항공안전교육원"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">🏢</InputAdornment>
                          ),
                        }}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                        helperText="교육기관명 입력"
                      />
                    </GridAny>
                  </GridAny>
                </Paper>
              </GridAny>

              {/* 섹션 2: 교육과정 분류정보 */}
              <GridAny item xs={12}>
                <Paper sx={{ 
                  p: 3, 
                  borderRadius: 3, 
                  border: '2px solid #fce4ec',
                  bgcolor: 'white',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
                }}>
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 1.5, 
                    mb: 3,
                    pb: 2,
                    borderBottom: '2px solid #fce4ec'
                  }}>
                    <Box sx={{ 
                      bgcolor: '#e91e63', 
                      color: 'white', 
                      p: 1, 
                      borderRadius: 2,
                      display: 'flex'
                    }}>
                      <School />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#344767' }}>
                        📚 교육과정 분류정보
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#7b809a' }}>
                        Training Classification (Required) - ICAO Annex 17 준수 필수
                      </Typography>
                    </Box>
                    <Chip 
                      label="필수입력" 
                      size="small" 
                      sx={{ 
                        bgcolor: '#fce4ec', 
                        color: '#e91e63', 
                        fontWeight: 600,
                        fontSize: '0.7rem'
                      }} 
                    />
                  </Box>
                  
                  <GridAny container spacing={3}>
                    <GridAny item xs={12} md={6}>
                      <FormControl fullWidth disabled={isSaving} required>
                        <InputLabel sx={{ fontWeight: 600 }}>
                          초기/정기 구분 *
                        </InputLabel>
                        <Select
                          value={selectedRecord.초기정기구분}
                          onChange={(e) => setSelectedRecord({ 
                            ...selectedRecord, 
                            초기정기구분: e.target.value as any 
                          })}
                          label="초기/정기 구분 *"
                          sx={{ 
                            borderRadius: 2,
                            '& .MuiSelect-select': { 
                              display: 'flex', 
                              alignItems: 'center', 
                              gap: 1 
                            }
                          }}
                        >
                          <MenuItem value="초기">
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                              <Chip 
                                label="초기" 
                                size="small" 
                                sx={{ bgcolor: '#e91e63', color: 'white', fontWeight: 600 }} 
                              />
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                  Initial Training
                                </Typography>
                                <Typography variant="caption" color="textSecondary">
                                  신규 취업자 대상
                                </Typography>
                              </Box>
                            </Box>
                          </MenuItem>
                          <MenuItem value="정기">
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                              <Chip 
                                label="정기" 
                                size="small" 
                                sx={{ bgcolor: '#1a73e8', color: 'white', fontWeight: 600 }} 
                              />
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                  Recurrent Training
                                </Typography>
                                <Typography variant="caption" color="textSecondary">
                                  기존 직원 정기교육
                                </Typography>
                              </Box>
                            </Box>
                          </MenuItem>
                          <MenuItem value="확인필요">
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                              <Chip 
                                label="확인필요" 
                                size="small" 
                                sx={{ bgcolor: '#e0e0e0', color: '#344767', fontWeight: 600 }} 
                              />
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                Verification Required
                              </Typography>
                            </Box>
                          </MenuItem>
                        </Select>
                      </FormControl>
                      <Typography variant="caption" sx={{ color: '#7b809a', mt: 0.5, display: 'block' }}>
                        💡 <strong>ICAO Annex 17</strong> 기준: 초기(Initial) 또는 정기(Recurrent)
                      </Typography>
                    </GridAny>

                    <GridAny item xs={12} md={6}>
                      <FormControl fullWidth disabled={isSaving} required>
                        <InputLabel sx={{ fontWeight: 600 }}>
                          보안책임자/감독자 *
                        </InputLabel>
                        <Select
                          value={selectedRecord.보안책임자감독자}
                          onChange={(e) => setSelectedRecord({ 
                            ...selectedRecord, 
                            보안책임자감독자: e.target.value as any 
                          })}
                          label="보안책임자/감독자 *"
                          sx={{ 
                            borderRadius: 2,
                            '& .MuiSelect-select': { 
                              display: 'flex', 
                              alignItems: 'center', 
                              gap: 1 
                            }
                          }}
                        >
                          <MenuItem value="보안책임자">
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                              <Chip 
                                label="책임자" 
                                size="small" 
                                sx={{ bgcolor: '#fb8c00', color: 'white', fontWeight: 600 }} 
                              />
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                  보안책임자 (Security Manager)
                                </Typography>
                                <Typography variant="caption" color="textSecondary">
                                  항공보안법 제16조
                                </Typography>
                              </Box>
                            </Box>
                          </MenuItem>
                          <MenuItem value="보안감독자">
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                              <Chip 
                                label="감독자" 
                                size="small" 
                                sx={{ bgcolor: '#344767', color: 'white', fontWeight: 600 }} 
                              />
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                  보안감독자 (Security Supervisor)
                                </Typography>
                                <Typography variant="caption" color="textSecondary">
                                  항공보안법 제17조
                                </Typography>
                              </Box>
                            </Box>
                          </MenuItem>
                          <MenuItem value="확인필요">
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                              <Chip 
                                label="확인필요" 
                                size="small" 
                                sx={{ bgcolor: '#e0e0e0', color: '#344767', fontWeight: 600 }} 
                              />
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                Verification Required
                              </Typography>
                            </Box>
                          </MenuItem>
                        </Select>
                      </FormControl>
                      <Typography variant="caption" sx={{ color: '#7b809a', mt: 0.5, display: 'block' }}>
                        💡 <strong>항공보안법</strong> 제16조/제17조 기준 역할 구분
                      </Typography>
                    </GridAny>
                  </GridAny>
                </Paper>
              </GridAny>

              {/* 섹션 3: 관리정보 */}
              <GridAny item xs={12}>
                <Paper sx={{ 
                  p: 3, 
                  borderRadius: 3, 
                  border: '2px solid #e8f5e9',
                  bgcolor: 'white',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
                }}>
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 1.5, 
                    mb: 3,
                    pb: 2,
                    borderBottom: '2px solid #e8f5e9'
                  }}>
                    <Box sx={{ 
                      bgcolor: '#4caf50', 
                      color: 'white', 
                      p: 1, 
                      borderRadius: 2,
                      display: 'flex'
                    }}>
                      <Security />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#344767' }}>
                        ⚙️ 관리정보
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#7b809a' }}>
                        Management Information (Optional) - 내부 관리용 데이터
                      </Typography>
                    </Box>
                    <Chip 
                      label="선택입력" 
                      size="small" 
                      sx={{ 
                        bgcolor: '#e8f5e9', 
                        color: '#4caf50', 
                        fontWeight: 600,
                        fontSize: '0.7rem'
                      }} 
                    />
                  </Box>
                  
                  <GridAny container spacing={3}>
                    <GridAny item xs={12} md={6}>
                      <TextField
                        label="소속 팀/지점"
                        value={selectedRecord.소속팀지점 || ''}
                        onChange={(e) => setSelectedRecord({ 
                          ...selectedRecord, 
                          소속팀지점: e.target.value 
                        })}
                        fullWidth
                        placeholder="예: 본사, 김포지점, 인천공항지점, 운항팀 등"
                        variant="outlined"
                        disabled={isSaving}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              🏢
                            </InputAdornment>
                          ),
                        }}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                        helperText="조직도 기준 소속 부서를 입력하세요"
                      />
                    </GridAny>

                    <GridAny item xs={12} md={6}>
                      <FormControl fullWidth disabled={isSaving}>
                        <InputLabel sx={{ fontWeight: 600 }}>처리 상태</InputLabel>
                        <Select
                          value={selectedRecord.처리상태}
                          onChange={(e) => setSelectedRecord({ 
                            ...selectedRecord, 
                            처리상태: e.target.value as any 
                          })}
                          label="처리 상태"
                          sx={{ 
                            borderRadius: 2,
                            '& .MuiSelect-select': { 
                              display: 'flex', 
                              alignItems: 'center', 
                              gap: 1 
                            }
                          }}
                        >
                          <MenuItem value="자동처리">
                            <Chip 
                              label="자동" 
                              size="small" 
                              sx={{ bgcolor: '#1a73e8', color: 'white', fontWeight: 600, mr: 1 }} 
                            />
                            🤖 자동처리 (Auto-processed)
                          </MenuItem>
                          <MenuItem value="수동입력">
                            <Chip 
                              label="수동" 
                              size="small" 
                              sx={{ bgcolor: '#fb8c00', color: 'white', fontWeight: 600, mr: 1 }} 
                            />
                            ✍️ 수동입력 (Manual Entry)
                          </MenuItem>
                          <MenuItem value="확인필요">
                            <Chip 
                              label="확인필요" 
                              size="small" 
                              sx={{ bgcolor: '#e91e63', color: 'white', fontWeight: 600, mr: 1 }} 
                            />
                            ⚠️ 확인필요 (Needs Verification)
                          </MenuItem>
                          <MenuItem value="완료">
                            <Chip 
                              label="완료" 
                              size="small" 
                              sx={{ bgcolor: '#4caf50', color: 'white', fontWeight: 600, mr: 1 }} 
                            />
                            ✅ 완료 (Completed)
                          </MenuItem>
                        </Select>
                      </FormControl>
                      <Typography variant="caption" sx={{ color: '#7b809a', mt: 0.5, display: 'block' }}>
                        💡 데이터 처리 이력 추적용
                      </Typography>
                    </GridAny>

                    <GridAny item xs={12}>
                      <TextField
                        label="비고 (Additional Notes)"
                        value={selectedRecord.비고 || ''}
                        onChange={(e) => setSelectedRecord({ 
                          ...selectedRecord, 
                          비고: e.target.value 
                        })}
                        multiline
                        rows={3}
                        fullWidth
                        placeholder="특이사항, 추가 메모, 감사 대응 사항 등을 입력하세요..."
                        disabled={isSaving}
                        sx={{ 
                          '& .MuiOutlinedInput-root': { 
                            borderRadius: 2,
                            bgcolor: '#fafafa'
                          } 
                        }}
                        helperText="이 정보는 내부 관리용이며 외부 보고서에 포함되지 않습니다"
                      />
                    </GridAny>
                  </GridAny>
                </Paper>
              </GridAny>
            </GridAny>
          )}
        </DialogContent>
        
        <DialogActions sx={{ 
          p: 3, 
          borderTop: '2px solid #e9ecef',
          bgcolor: 'white',
          gap: 1.5,
          justifyContent: 'space-between'
        }}>
          <Typography variant="caption" sx={{ color: '#7b809a', flex: 1 }}>
            * 필수 항목은 반드시 입력해야 저장됩니다
          </Typography>
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <Button 
              onClick={() => setEditDialogOpen(false)} 
              variant="outlined"
              sx={{ 
                color: '#7b809a', 
                borderColor: '#e0e0e0',
                textTransform: 'none',
                px: 3,
                fontWeight: 600,
                '&:hover': {
                  borderColor: '#7b809a',
                  bgcolor: '#f8f9fa'
                }
              }} 
              disabled={isSaving}
              startIcon={<Close />}
            >
              취소
            </Button>
            <Button 
              onClick={handleSave} 
              variant="contained" 
              disabled={isSaving}
              startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : <Save />}
              sx={{ 
                bgcolor: '#344767', 
                px: 4,
                textTransform: 'none',
                fontWeight: 700,
                boxShadow: '0 4px 12px rgba(52,71,103,0.3)',
                '&:hover': { 
                  bgcolor: '#212529',
                  boxShadow: '0 6px 16px rgba(52,71,103,0.4)'
                },
                '&:disabled': { 
                  bgcolor: '#7b809a',
                  boxShadow: 'none'
                }
              }}
            >
              {isSaving ? '저장 중...' : '💾 저장하기'}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* 스낵바 */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          severity={snackbar.severity} 
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          variant="filled"
          sx={{ 
            width: '100%',
            borderRadius: 2,
            fontWeight: 600
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default TrainingDataTable;
